/**
 * useConfirmAlert Hook
 *
 * Manages confirm and alert modal state. Used across pages that render
 * ConfirmModal and AlertModal components for user confirmations and alerts.
 *
 * @module hooks/useConfirmAlert
 */

import { useState, useCallback } from 'react';

/**
 * Hook to manage confirm and alert modal state
 *
 * @returns {{
 *   confirmModal: null | { message: string, onConfirm: Function, type?: string, confirmText?: string, confirmStyle?: string },
 *   alertModal: null | { message: string, type?: string, copyUrl?: string },
 *   setConfirmModal: Function,
 *   setAlertModal: Function,
 *   closeConfirm: Function,
 *   closeAlert: Function
 * }}
 *
 * @example
 * function MyPage() {
 *   const { confirmModal, alertModal, setConfirmModal, setAlertModal, closeConfirm, closeAlert } = useConfirmAlert();
 *
 *   const handleDelete = () => {
 *     setConfirmModal({
 *       message: 'Are you sure you want to delete this?',
 *       onConfirm: () => { deleteItem(); closeConfirm(); },
 *       confirmText: 'Delete',
 *       confirmStyle: 'danger',
 *     });
 *   };
 *
 *   return (
 *     <>
 *       <button onClick={handleDelete}>Delete</button>
 *       {confirmModal && (
 *         <ConfirmModal
 *           message={confirmModal.message}
 *           onConfirm={confirmModal.onConfirm}
 *           onCancel={closeConfirm}
 *           confirmText={confirmModal.confirmText || "Confirm"}
 *           confirmStyle={confirmModal.confirmStyle || "danger"}
 *         />
 *       )}
 *       {alertModal && (
 *         <AlertModal
 *           message={alertModal.message}
 *           type={alertModal.type}
 *           copyUrl={alertModal.copyUrl}
 *           onClose={closeAlert}
 *         />
 *       )}
 *     </>
 *   );
 * }
 */
export function useConfirmAlert() {
    const [confirmModal, setConfirmModal] = useState(null); // { message, onConfirm, type, confirmText, confirmStyle }
    const [alertModal, setAlertModal] = useState(null); // { message, type, copyUrl }

    const closeConfirm = useCallback(() => setConfirmModal(null), []);
    const closeAlert = useCallback(() => setAlertModal(null), []);

    return {
        confirmModal,
        alertModal,
        setConfirmModal,
        setAlertModal,
        closeConfirm,
        closeAlert,
    };
}
